import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Button from './ui/Button';
import Icon from './AppIcon';
import AreaCodePicker from './AreaCodePicker';
import { getAuthHeaders } from '../lib/api';
import { PHONE_CHARGE_WHEN_ASSIGNED } from '../lib/billing-copy';
import { supabase } from '../lib/supabase';
import { normalizeNaPhoneCountry } from '../lib/na-phone-countries';

const PhoneNumberOnboarding = ({ onComplete, userId, accessToken, initialCountry = 'US' }) => {
  const { user: authUser, loading: authLoading } = useAuth();
  const user = userId ? { id: userId } : authUser;
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [areaCode, setAreaCode] = useState('');
  const [country, setCountry] = useState(() => normalizeNaPhoneCountry(initialCountry));
  const [error, setError] = useState('');
  const [purchasedNumber, setPurchasedNumber] = useState(null);
  const [availabilityStatus, setAvailabilityStatus] = useState(null);
  const [savedAreaCodeOnly, setSavedAreaCodeOnly] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [twilioAvailable, setTwilioAvailable] = useState(null);
  const [alternatives, setAlternatives] = useState([]);

  // Show loading if auth is still loading and no userId prop provided
  if (authLoading && !userId) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Loader2" size={32} className="text-primary animate-spin" />
        </div>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // Show error if user is not available
  if (!user?.id) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <p className="text-destructive text-sm">
            User not authenticated. Please go back and complete the registration process.
          </p>
        </div>
      </div>
    );
  }

  // Check if agent already has a number on mount.
  // During onboarding (userId or accessToken from Register) we skip this to avoid 403: the client may still have
  // an old session token, so we never call assign-phone-number here. User just enters area code
  // and we save it via save-area-code (which uses token user only).
  useEffect(() => {
    if (userId || accessToken) {
      // Onboarding flow: do not call assign-phone-number (token often doesn't match new user yet)
      return;
    }
    const checkExistingNumber = async () => {
      const currentUserId = authUser && authUser.id;
      if (!currentUserId) return;

      const headers = {
        'Content-Type': 'application/json',
      };
      const authHeaders = await getAuthHeaders();
      Object.assign(headers, authHeaders);

      try {
        const response = await fetch('/api/agents/assign-phone-number', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            agentId: currentUserId,
            country: 'US',
          }),
        });

        const data = await response.json();

        if (response.ok && data.success && data.availabilityStatus === 'already_assigned') {
          setPurchasedNumber(data.phoneNumber);
          setAvailabilityStatus(data.availabilityStatus);
          setStep(2);
        } else if (response.ok && data.success === false && data.hasNumber === false) {
          if (data.preferredAreaCode) setAreaCode(data.preferredAreaCode);
          if (data.preferredCountry) setCountry(normalizeNaPhoneCountry(data.preferredCountry));
        }
      } catch (err) {
        console.log('No existing number found, proceeding with onboarding');
      }
    };

    checkExistingNumber();
  }, [userId, accessToken, authUser && authUser.id]);

  const buildAuthHeaders = useCallback(async () => {
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    } else if (!userId) {
      const authHeaders = await getAuthHeaders();
      Object.assign(headers, authHeaders);
    } else {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }
    }
    return headers;
  }, [accessToken, userId]);

  const handlePickerStatusChange = useCallback(({ checkingAvailability: checking, twilioAvailable: available, alternatives: alts }) => {
    setCheckingAvailability(checking);
    setTwilioAvailable(available);
    setAlternatives(alts);
  }, []);

  const handleContinue = async () => {
    const selectedAreaCode = areaCode;
    
    if (!selectedAreaCode || selectedAreaCode.length !== 3) {
      setError('Please enter a 3-digit area code');
      return;
    }

    const currentUserId = userId || authUser?.id;
    
    if (!currentUserId) {
      setError('User not authenticated. Please refresh the page and try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const headers = await buildAuthHeaders();
      if (!headers.Authorization && userId) {
        setLoading(false);
        setError('Session is still loading. Please wait a moment and try again.');
        return;
      }
      // Save preferred area code only; number is assigned after A2P is approved (API uses token user, no agentId needed)
      const response = await fetch('/api/agents/save-area-code', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          areaCode: selectedAreaCode,
          country,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.alternatives?.length) {
          setAlternatives(data.alternatives);
          setTwilioAvailable(false);
        }
        const errorMessage = data.error || 'Failed to save area code';
        throw new Error(errorMessage);
      }

      if (data.success) {
        setSavedAreaCodeOnly(true);
        setStep(2);
      } else {
        throw new Error(data.error || 'Failed to save area code');
      }
    } catch (err) {
      console.error('Error saving area code:', err);
      setError(err.message || 'Failed to save area code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    onComplete?.();
  };

  if (step === 1) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Phone" size={32} className="text-primary" />
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-2 text-center">Choose Your Phone Number</h2>
        <p className="text-muted-foreground mb-6 text-center">
          Pick your {country === 'CA' ? 'province' : 'state'} to see available area codes, then select one for your dedicated SMS number.
        </p>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 mb-4">
            <p className="text-destructive text-sm whitespace-pre-line">{error}</p>
          </div>
        )}

        {/* Area code search */}
        <AreaCodePicker
          value={areaCode}
          onChange={(code) => { setAreaCode(code); setError(''); }}
          country={country}
          onCountryChange={setCountry}
          getHeaders={buildAuthHeaders}
          onStatusChange={handlePickerStatusChange}
          unavailableHint="You can still continue — we'll search nearby area codes when your number is assigned after AI Compliance approval. Or pick an alternative above."
        />

        <div className="flex justify-center mt-6">
          <Button
            onClick={handleContinue}
            loading={loading}
            disabled={!areaCode || areaCode.length !== 3 || checkingAvailability}
            className="w-full sm:w-auto"
          >
            {twilioAvailable === false ? 'Continue with nearest available' : 'Continue'}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-4 text-center">
          Your number is assigned after AI Compliance approval.
        </p>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="max-w-md mx-auto text-center p-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} className="text-green-600" />
        </div>
        
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {availabilityStatus === 'already_assigned' ? 'Your Phone Number' : savedAreaCodeOnly ? 'Area Code Saved' : 'Phone Number Assigned!'}
        </h2>
        
        {purchasedNumber && (
          <div className="text-left bg-card border border-border rounded-lg p-4 mb-6">
            <h3 className="font-medium text-foreground mb-2">Your SMS Number:</h3>
            <p className="text-2xl font-bold text-primary">
              {purchasedNumber}
            </p>
            {availabilityStatus && (
              <p className="text-xs text-muted-foreground mt-2">
                {availabilityStatus === 'exact_match' && '✓ Your preferred area code'}
                {availabilityStatus === 'fallback' && '⚠ Alternative area code assigned'}
                {availabilityStatus === 'random' && 'ℹ Random area code assigned'}
                {availabilityStatus === 'already_assigned' && '✓ Your existing number'}
              </p>
            )}
          </div>
        )}
        
        {savedAreaCodeOnly && !purchasedNumber && (
          <div className="text-left bg-card border border-border rounded-lg p-4 mb-6">
            <p className="text-foreground">
              Your preferred area code <strong>({areaCode})</strong> in {country === 'CA' ? 'Canada' : 'the US'} is saved. We&apos;ll assign your dedicated SMS number once AI Compliance is approved.
            </p>
            <p className="text-xs text-muted-foreground mt-2">{PHONE_CHARGE_WHEN_ASSIGNED}</p>
          </div>
        )}
        
        {purchasedNumber && (
          <p className="text-sm text-foreground/80 bg-muted/50 rounded-lg px-4 py-3 mb-4">{PHONE_CHARGE_WHEN_ASSIGNED}</p>
        )}
        
        <p className="text-muted-foreground mb-6">
          {availabilityStatus === 'already_assigned' 
            ? 'Your dedicated SMS number is ready to use. All your client communications will use this number.'
            : savedAreaCodeOnly
            ? 'Complete AI Compliance in the next step. Your number will be assigned automatically after approval.'
            : 'Your dedicated SMS number is ready! All your client communications will use this number.'}
        </p>
        
        <div className="space-y-3">
          <Button 
            onClick={handleComplete}
            loading={loading}
            className="w-full"
          >
            Continue Setup
          </Button>
          
          <p className="text-xs text-muted-foreground">
            You can view or update your number in settings at any time.
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default PhoneNumberOnboarding;

